const API_URL =
  import.meta.env.VITE_API_URL ||
  "http://localhost:5000/v1";

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-App-Origin": window.location.origin,
      ...options.headers,
    };

    // Remove Content-Type for FormData to let browser set boundary
    if (options.body instanceof FormData) {
      delete headers["Content-Type"];
    }

    const config: RequestInit = {
      ...options,
      headers,
      // Include credentials (cookies) in all requests
      credentials: "include",
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.message ||
            errorData.error?.message ||
            `API Error: ${response.statusText}`,
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any).response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      return response.json();
    } catch (error) {
      console.error("API Request Failed:", error);
      throw error;
    }
  }

  get<T>(endpoint: string, options?: FetchOptions) {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  post<T>(endpoint: string, data: unknown, options?: FetchOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put<T>(endpoint: string, data: unknown, options?: FetchOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data: unknown, options?: FetchOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string, options?: FetchOptions) {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  upload<T>(endpoint: string, file: File, options?: FetchOptions) {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: formData,
    });
  }
}

export const api = new ApiClient(API_URL);
